import {
  type DemoSessionActivity,
  type GetDemoSessionBreakdownArgs,
  useInfiniteScrollFromCache,
  useLazyGetDemoSessionsBreakdownQuery,
} from '@carboncopyco/data';
import {
  TextBase,
  TextCaption,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import {
  formatPlural,
  getTimezoneName,
  secondsToHHmmss,
} from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { TimestampCol } from '../../../Dashboard/SessionBreakdownComponents';
import type { CsvParams } from '../../../Dashboard/SessionBreakdownComponents/types';
import type { Col } from '../../../FixedTable';
import { useUrlState } from '../../../utils';
import { OpportunitiesFilter } from '../../Controls/OpportunitiesFilter';
import { PresentersFilter } from '../../Controls/PresentersFilter';
import { SessionsBreakdownTable } from '../../SessionsBreakdownTable';
import type { UrlState } from '../../types';

const cols: Col<DemoSessionActivity>[] = [
  {
    alignment: 'left',
    title: 'Date & time',
    width: 270,
    maxContentWidth: 250,
    render: ({ timestamp }) => <TimestampCol timestamp={timestamp} />,
  },
  {
    alignment: 'left',
    title: 'Presenter',
    width: 260,
    render: ({ presenter }) => (
      <UserBasicInfo
        user={presenter}
        textColor="text-ds-natural-600"
        truncateWidth={130}
        size="small"
        tooltip
      />
    ),
  },
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 220,
    maxContentWidth: 190,
    render: ({ forClient }) => (
      <TooltipBubble
        title={forClient}
        placement="top-start"
      >
        <div className="truncate">
          <TextBase
            label={forClient}
            color="text-ds-natural-600"
            ellipsis
          />
        </div>
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: 'Duration',
    width: 200,
    render: (presentation) => (
      <TextBase
        label={secondsToHHmmss(presentation.durationInSeconds)}
        color="text-ds-natural-600"
      />
    ),
  },
];

type PlaybookSessionsBreakdownType = {
  organizationId: string;
  workspaceId: string;
  setCsvParams: (params: CsvParams) => void;
  playbookId: string;
};

export const PlaysSessionsBreakdownTable = ({
  organizationId,
  workspaceId,
  setCsvParams,
  playbookId,
}: PlaybookSessionsBreakdownType) => {
  const [showAll, setShowAll] = useState(false);
  const [urlState, setUrlState] = useUrlState<UrlState>({});
  const { presenters, opportunities } = urlState;

  const fetchArgs = useMemo<GetDemoSessionBreakdownArgs>(
    () => ({
      organizationId,
      shortSessions: true,
      internalSessions: true,
      workspaceIds: [workspaceId],
      timezone: getTimezoneName(),
      opportunities: opportunities ? opportunities.split(',') : undefined,
      presenters: presenters ? presenters.split(',') : undefined,
      playbookId,
      withScreenshots: false,
      resourceTypes: ['play'],
    }),
    [playbookId, opportunities, organizationId, presenters, workspaceId]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<
    DemoSessionActivity,
    GetDemoSessionBreakdownArgs
  >(useLazyGetDemoSessionsBreakdownQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs, fetchFirstBatch]);

  const clearFilters = () => {
    setUrlState({
      ...urlState,
      presenters: undefined,
      opportunities: undefined,
    });
  };

  useEffect(() => {
    setCsvParams({
      params: {
        ...fetchArgs,
        resourceTypes: playbookId ? ['play'] : ['managed_presentation'],
      },
      isDisabled: (items && !items.length) || error,
    });
  }, [items, fetchArgs, error, setCsvParams, playbookId]);

  return (
    <>
      <div className="w-full flex items-center justify-between px-6 py-5">
        <div className="flex gap-3">
          <PresentersFilter
            organizationId={organizationId}
            workspaceId={workspaceId}
            playbookId={playbookId}
          />
          <OpportunitiesFilter
            organizationId={organizationId}
            workspaceId={workspaceId}
            playbookId={playbookId}
          />
        </div>
        {!!items?.length && (
          <TextCaption
            color="text-ds-natural-500"
            label={`${
              totalSize > 7 && !showAll ? 7 : items.length
            } ${formatPlural('session', items.length)} (out of ${totalSize})`}
          />
        )}
      </div>
      <SessionsBreakdownTable
        showAll={showAll}
        setShowAll={setShowAll}
        totalSize={totalSize}
        onClear={clearFilters}
        error={error}
        handleFetchNextBatch={fetchNextBatch}
        cols={cols}
        data={items}
        isLoading={isLoading}
        isLoadingBatch={isLoadingBatch}
        noResults={(presenters || opportunities) && !items?.length}
      />
    </>
  );
};
